import React, { useState, useRef, useEffect, useMemo } from 'react'
import Header from '../../components/common/header/Header'
import '../analytics/Analytics.css'
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { Gauge } from '@mui/x-charts/Gauge';
import VehicleCard from '../../components/vehicleCard/VehicleCard';
import analyticsData from '../../data/Analytics_data.json';
import search from '../../images/search.svg';
import sort from '../../images/sort.svg';

import { getRandomDate } from '../../utils/date.utils';

function Analytics() {
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Generate dynamic timestamps on every component mount
    const processedAnalyticsData = useMemo(() =>
        analyticsData.map(item => ({
            ...item,
            timestamp: getRandomDate()
        })), []
    );

    const selectedVehicle = processedAnalyticsData[selectedIndex];

    const coolantValue = selectedVehicle.coolantTemperature;
    let coolantColor = '#24A52F'; // green
    if (coolantValue >= 90) {
        coolantColor = '#D21212'; // red
    } else if (coolantValue >= 76) {
        coolantColor = '#FFA500'; // orange
    }

    const intakeValue = selectedVehicle.intakeTemperature;
    let intakeColor = '#24A52F'; // green
    if (intakeValue >= 90) {
        intakeColor = '#D21212'; // red
    } else if (intakeValue >= 76) {
        intakeColor = '#FFA500'; // orange
    }

    // Chart duration selectors
    const [mileageDuration, setMileageDuration] = useState('Year');
    const [idlingDuration, setIdlingDuration] = useState('Year');
    const [fuelRateDuration, setFuelRateDuration] = useState('Year');
    const [milesDuration, setMilesDuration] = useState('Week');
    const [torqueDuration, setTorqueDuration] = useState('Week');
    const [loadDuration, setLoadDuration] = useState('Year');
    const [brakingDuration, setBrakingDuration] = useState('Week');

    const [searchQuery, setSearchQuery] = useState('');

    // Filter vehicles based on search query
    const filteredVehicles = processedAnalyticsData.filter(vehicle => {
        const searchLower = searchQuery.toLowerCase();
        return (
            vehicle.vehicle.toLowerCase().includes(searchLower) ||
            vehicle.deviceId.toLowerCase().includes(searchLower) ||
            vehicle.licensePlate.toLowerCase().includes(searchLower)
        );
    });

    // Helper for x-axis labels (dynamic by data length, always matches data length)
    const getLabels = (duration, type, dataLength) => {
        if (duration === 'Year') {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return months.slice(0, dataLength).concat(
                Array.from({ length: Math.max(0, dataLength - months.length) }, (_, i) => `M${months.length + i + 1}`)
            );
        } else if (duration === 'Month') {
            // Generate last N days as MM/DD, ending with today
            const today = new Date();
            return Array.from({ length: dataLength }, (_, i) => {
                const d = new Date(today);
                d.setDate(today.getDate() - (dataLength - 1 - i));
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                return `${mm}/${dd}`;
            });
        } else if (duration === 'Week') {
            const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            return weekDays.slice(0, dataLength);
        } else if (duration === 'Day') {
            // Generate last N hours as HH:00, ending with current hour
            const now = new Date();
            return Array.from({ length: dataLength }, (_, i) => {
                const d = new Date(now);
                d.setHours(now.getHours() - (dataLength - 1 - i));
                const hh = String(d.getHours()).padStart(2, '0');
                return `${hh}:00`;
            });
        }
        return Array(dataLength).fill('');
    };

    // Helper to get chart data by duration
    function getChartData(chartArray, duration) {
        if (!Array.isArray(chartArray)) return [];
        const found = chartArray.find(item => item.duration === duration);
        return found ? found.data : [];
    }

    // Chart data from selectedVehicle (duration-aware)
    const mileageData = getChartData(selectedVehicle.charts.mileage, mileageDuration);
    const mileageLabels = getLabels(mileageDuration, 'mileage', mileageData.length);
    const idlingData = getChartData(selectedVehicle.charts.idlingBreakdown, idlingDuration);
    const idlingLabels = getLabels(idlingDuration, 'idlingBreakdown', idlingData.length);
    const fuelRateData = getChartData(selectedVehicle.charts.engineFuelRate, fuelRateDuration);
    const fuelRateLabels = getLabels(fuelRateDuration, 'engineFuelRate', fuelRateData.length);
    const milesData = getChartData(selectedVehicle.charts.milesTravelled, milesDuration);
    const milesLabels = getLabels(milesDuration, 'milesTravelled', milesData.length);
    const torqueData = getChartData(selectedVehicle.charts.engineTorque, torqueDuration);
    const torqueLabels = getLabels(torqueDuration, 'engineTorque', torqueData.length);
    const loadData = getChartData(selectedVehicle.charts.engineLoad, loadDuration);
    const loadLabels = getLabels(loadDuration, 'engineLoad', loadData.length);
    const brakingData = getChartData(selectedVehicle.charts.harshBraking, brakingDuration);
    const brakingLabels = getLabels(brakingDuration, 'harshBraking', brakingData.length);

    const sidebarRef = useRef(null);
    const cardRefs = useRef([]);

    // Auto-scroll to selected card
    useEffect(() => {
        if (cardRefs.current[selectedIndex]) {
            cardRefs.current[selectedIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [selectedIndex, filteredVehicles]);

    return (
        <div className='container-m'>
            <Header />
            <div className="main-container" style={{ display: 'flex', width: '100%', height: 'calc(100vh - 64px)' }}>
                {/* Left Sidebar */}
                <div
                    className="sidebar"
                    ref={sidebarRef}
                    style={{ flex: '0 0 364px', maxWidth: '364px', minWidth: '364px', backgroundColor: '#171A20', display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                    <header className="input-group " style={{ width: "98%", background: '#171A20', border: '0.2px solid #D7E5DE', borderRadius: '6px', padding: "2px" }}>
                        <span className="input-group-text" style={{ background: '#171A20', border: 'none' }}>
                            <img src={search} alt="search" style={{ background: '#171A20' }} />
                        </span>
                        <input type="text" className="form-control" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ background: '#171A20', color: '#fff', border: 'none', '::placeholder': { color: '#7B9999' } }} />
                        <span className="input-group-text" style={{ background: '#171A20', border: 'none' }}>
                            <img src={sort} alt="sort" style={{ background: '#171A20' }} />
                        </span>
                    </header>
                    <footer className="overflow-auto" style={{ flex: 1, maxHeight: 'none' }}>
                        {filteredVehicles.map((v, index) => {
                            const imageUrl = v.imageUrl || '';
                            return (
                                <div
                                    key={index}
                                    ref={el => { cardRefs.current[index] = el; }}
                                    style={{ marginBottom: '10px', marginLeft: '5px', marginRight: '5px' }}
                                >
                                    <VehicleCard
                                        vehicleId={v.vehicleId}
                                        vehicleBasicInfo={{ ...v, imageUrl }}
                                        expandMetrics={selectedIndex === index}
                                        hideMoreInfoButton={true}
                                        onClickVehicleCard={() => {
                                            setSelectedIndex(index);
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </footer>
                </div>
                {/* Chart Container */}
                <div className="chart_container" style={{ flex: 1, padding: '20px', backgroundColor: '#1F252F', alignItems: 'center', height: '100%', overflowY: 'auto' }}>
                    <div className="container1" style={{ display: "flex", justifyContent: "space-between" }}>
                        <div className="chart1" style={{ backgroundColor: '#070C14', minWidth: '500px', margin: '5px', border: '1px solid #9AB2F9', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div className="g1">
                                <Gauge
                                    value={selectedVehicle.load}
                                    startAngle={-150}
                                    endAngle={150}
                                    height={200}
                                    width={200}
                                    sx={{
                                        '& .MuiGauge-valueText': {
                                            fontSize: 20,
                                            transform: 'translate(5px, 0px)',
                                            fill: '#fff !important',
                                            color: '#fff !important',
                                        },
                                        '& text': {
                                            fill: '#fff !important',
                                            color: '#fff !important',
                                        },
                                        '& .MuiGauge-referenceArc': {
                                            fill: '#2A2A2A',
                                        },
                                        '& .MuiGauge-valueArc': {
                                            fill: '#949FFF',
                                        }
                                    }}
                                    text={({ value }) => `${value}`}
                                />
                                <h4 style={{ color: '#fff', textAlign: 'center', fontSize: '16px' }}>Load (%)</h4>
                            </div>
                            <div className="g2">
                                <Gauge
                                    value={selectedVehicle.torque}
                                    startAngle={-150}
                                    endAngle={150}
                                    height={200}
                                    width={200}
                                    sx={{
                                        '& .MuiGauge-valueText': {
                                            fontSize: 20,
                                            transform: 'translate(5px, 0px)',
                                            fill: '#fff !important',
                                            color: '#fff !important',
                                        },
                                        '& text': {
                                            fill: '#fff !important',
                                            color: '#fff !important',
                                        },
                                        '& .MuiGauge-referenceArc': {
                                            fill: '#2A2A2A',
                                        },
                                        '& .MuiGauge-valueArc': {
                                            fill: '#949FFF',
                                        }
                                    }}
                                    text={({ value }) => `${value}`}
                                />
                                <h4 style={{ color: '#fff', textAlign: 'center', fontSize: '16px' }}>Torque (%)</h4>
                            </div>

                        </div>

                        <div className="chart2" style={{ backgroundColor: '#070C14', minWidth: '500px', margin: '5px', border: '1px solid #9AB2F9', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div className="g3">
                                <Gauge
                                    value={coolantValue}
                                    startAngle={-150}
                                    endAngle={150}
                                    height={200}
                                    width={200}
                                    sx={{
                                        '& .MuiGauge-valueText': {
                                            fontSize: 20,
                                            transform: 'translate(5px, 0px)',
                                            fill: '#fff !important',
                                            color: '#fff !important',
                                        },
                                        '& text': {
                                            fill: '#fff !important',
                                            color: '#fff !important',
                                        },
                                        '& .MuiGauge-referenceArc': {
                                            fill: '#2A2A2A',
                                        },
                                        '& .MuiGauge-valueArc': {
                                            fill: coolantColor,
                                        }
                                    }}
                                    text={({ value }) => `${value}°C`}
                                />
                                <h4 style={{ color: '#fff', textAlign: 'center', fontSize: '16px' }}>Coolant Temperature</h4>
                            </div>
                            <div className="g4">
                                <Gauge
                                    value={intakeValue}
                                    startAngle={-150}
                                    endAngle={150}
                                    height={200}
                                    width={200}
                                    sx={{
                                        '& .MuiGauge-valueText': {
                                            fontSize: 20,
                                            transform: 'translate(5px, 0px)',
                                            fill: '#fff !important',
                                            color: '#fff !important',
                                        },
                                        '& text': {
                                            fill: '#fff !important',
                                            color: '#fff !important',
                                        },
                                        '& .MuiGauge-referenceArc': {
                                            fill: '#2A2A2A',
                                        },
                                        '& .MuiGauge-valueArc': {
                                            fill: intakeColor,
                                        }
                                    }}
                                    text={({ value }) => `${value}°C`}
                                />
                                <h4 style={{ color: '#fff', textAlign: 'center', fontSize: '16px' }}>Intake Temperature</h4>
                            </div>

                        </div>

                        <div className="chart3" style={{ backgroundColor: '#070C14', minWidth: '500px', margin: '5px', border: '1px solid #9AB2F9', borderRadius: '5px' }}>
                            <div className="a-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                                <h4 style={{ color: '#fff' }}>Mileage</h4>
                                <select value={mileageDuration} onChange={e => setMileageDuration(e.target.value)}>
                                    <option value="Year">Year</option>
                                    <option value="Month">Month</option>
                                    <option value="Week">Week</option>
                                    <option value="Day">Day</option>
                                </select>
                            </div>
                            <LineChart
                                xAxis={[{ data: mileageLabels, scaleType: 'band' }]}
                                series={[
                                    {
                                        data: mileageData,
                                        showMark: ({ index }) => index % 2 === 0,
                                        color: '#0C8ACC'
                                    },
                                ]}
                                height={250}
                                width={450}
                            />
                        </div>
                    </div>
                    <div className="container2" style={{ display: "flex", justifyContent: "space-between" }}>
                        <div className="chart4" style={{ backgroundColor: '#070C14', minWidth: '500px', margin: '5px', border: '1px solid #9AB2F9', borderRadius: '5px' }}>

                            <div className="a-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                                <h4 style={{ color: '#fff' }}>Idling Breakdown</h4>
                                <select value={idlingDuration} onChange={e => setIdlingDuration(e.target.value)}>
                                    <option value="Year">Year</option>
                                    <option value="Month">Month</option>
                                    <option value="Week">Week</option>
                                    <option value="Day">Day</option>
                                </select>
                            </div>
                            <BarChart
                                xAxis={[{ data: idlingLabels }]}
                                series={[{ data: idlingData, color: '#F8A6A6' }]}
                                height={250}
                                width={450}
                            />
                        </div>
                        <div className="chart5" style={{ backgroundColor: '#070C14', minWidth: '500px', margin: '5px', border: '1px solid #9AB2F9', borderRadius: '5px' }}>

                            <div className="a-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                                <h4 style={{ color: '#fff' }}>Engine Fuel Rate</h4>
                                <select value={fuelRateDuration} onChange={e => setFuelRateDuration(e.target.value)}>
                                    <option value="Year">Year</option>
                                    <option value="Month">Month</option>
                                    <option value="Week">Week</option>
                                    <option value="Day">Day</option>
                                </select>
                            </div>
                            <BarChart
                                xAxis={[{ data: fuelRateLabels }]}
                                series={[{ data: fuelRateData, color: '#949FFF' }]}
                                height={250}
                                width={450}
                            />
                        </div>
                        <div className="chart6" style={{ backgroundColor: '#070C14', minWidth: '500px', margin: '5px', border: '1px solid #9AB2F9', borderRadius: '5px' }}>

                            <div className="a-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                                <h4 style={{ color: '#fff' }}>Miles Travelled</h4>
                                <select value={milesDuration} onChange={e => setMilesDuration(e.target.value)}>
                                    <option value="Year">Year</option>
                                    <option value="Month">Month</option>
                                    <option value="Week">Week</option>
                                    <option value="Day">Day</option>
                                </select>
                            </div>
                            <LineChart
                                xAxis={[{ data: milesLabels, scaleType: 'band' }]}
                                series={[
                                    {
                                        data: milesData,
                                        showMark: ({ index }) => index % 2 === 0,
                                        color: '#FF718B'
                                    },
                                ]}
                                height={250}
                                width={450}
                            />
                        </div>
                    </div>
                    <div className="container3" style={{ display: "flex", justifyContent: "space-between" }}>
                        <div className="chart7" style={{ backgroundColor: '#070C14', minWidth: '500px', margin: '5px', border: '1px solid #9AB2F9', borderRadius: '5px' }}>

                            <div className="a-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                                <h4 style={{ color: '#fff' }}>Engine % Torque</h4>
                                <select value={torqueDuration} onChange={e => setTorqueDuration(e.target.value)}>
                                    <option value="Year">Year</option>
                                    <option value="Month">Month</option>
                                    <option value="Week">Week</option>
                                    <option value="Day">Day</option>
                                </select>
                            </div>
                            <LineChart
                                xAxis={[{ data: torqueLabels, scaleType: 'band' }]}
                                series={[
                                    {
                                        data: torqueData,
                                        showMark: ({ index }) => index % 2 === 0,
                                        color: '#FABB70'
                                    },
                                ]}
                                height={250}
                                width={450}
                            />
                        </div>
                        <div className="chart8" style={{ backgroundColor: '#070C14', minWidth: '500px', margin: '5px', border: '1px solid #9AB2F9', borderRadius: '5px' }}>

                            <div className="a-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                                <h4 style={{ color: '#fff' }}>Engine % Load</h4>
                                <select value={loadDuration} onChange={e => setLoadDuration(e.target.value)}>
                                    <option value="Year">Year</option>
                                    <option value="Month">Month</option>
                                    <option value="Week">Week</option>
                                    <option value="Day">Day</option>
                                </select>
                            </div>
                            <LineChart
                                xAxis={[{ data: loadLabels, scaleType: 'band' }]}
                                series={[
                                    {
                                        data: loadData,
                                        showMark: ({ index }) => index % 2 === 0,
                                        color: '#00A3FF'
                                    },
                                ]}
                                height={250}
                                width={450}
                            />
                        </div>
                        <div className="chart9" style={{ backgroundColor: '#070C14', minWidth: '500px', margin: '5px', border: '1px solid #9AB2F9', borderRadius: '5px' }}>

                            <div className="a-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                                <h4 style={{ color: '#fff' }}>Harsh Braking</h4>
                                <select value={brakingDuration} onChange={e => setBrakingDuration(e.target.value)}>
                                    <option value="Year">Year</option>
                                    <option value="Month">Month</option>
                                    <option value="Week">Week</option>
                                    <option value="Day">Day</option>
                                </select>
                            </div>
                            <LineChart
                                xAxis={[{ data: brakingLabels, scaleType: 'band' }]}
                                series={[
                                    {
                                        data: brakingData,
                                        showMark: ({ index }) => index % 2 === 0,
                                        color: '#D365D6'
                                    },
                                ]}
                                height={250}
                                width={450}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

    )
}

export default Analytics
